import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDiscoveryClient } from '../api/context';
import type { LeadEnrichment, LeadPipelineStage } from '../api/types';

export const leadsKey = (folderPublicId: string) =>
  ['discovery', 'folders', folderPublicId, 'leads'] as const;

export function useLeads(folderPublicId: string | null) {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: folderPublicId ? leadsKey(folderPublicId) : ['discovery', 'leads', 'none'],
    queryFn: () => {
      if (!folderPublicId) throw new Error('No folder selected');
      return client.leads.list(folderPublicId);
    },
    enabled: !!folderPublicId,
  });
}

export function useDeleteLead(folderPublicId: string) {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (username: string) => client.leads.delete(folderPublicId, username),
    onSuccess: () => qc.invalidateQueries({ queryKey: leadsKey(folderPublicId) }),
  });
}

export function useBulkDeleteLeads(folderPublicId: string) {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (usernames: string[]) => client.leads.deleteBulk(folderPublicId, usernames),
    onSuccess: () => qc.invalidateQueries({ queryKey: leadsKey(folderPublicId) }),
  });
}

export function useSaveBatchLeads(folderPublicId: string) {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (creators: LeadEnrichment[]) => client.leads.saveBatch(folderPublicId, creators),
    onSuccess: () => qc.invalidateQueries({ queryKey: leadsKey(folderPublicId) }),
  });
}

export function useMarkDmSent(folderPublicId: string) {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (username: string) => client.leads.markDmSent(folderPublicId, username),
    onSuccess: () => qc.invalidateQueries({ queryKey: leadsKey(folderPublicId) }),
  });
}

export interface LeadUpdateInput {
  username: string;
  pipeline?: LeadPipelineStage;
  notes?: string;
}

export function useUpdateLead(folderPublicId: string) {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, ...patch }: LeadUpdateInput) =>
      client.leads.update(folderPublicId, username, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: leadsKey(folderPublicId) }),
  });
}
